import React from "react";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import { Box, useTheme } from "@mui/material";
import {
  RiFileListLine,
  RiAlertLine,
  RiSettings3Line,
  RiCheckboxCircleLine,
  RiInformationLine,
  RiFileWarningLine,
} from "@remixicon/react";
import { RiCalendarLine } from "@remixicon/react";

/**
 * Metric type determines the icon and color behavior of the SummaryCard
 * - 'total': Blue color, file list icon (for counts/totals)
 * - 'issues': Dynamic color (green if 0, orange if >0), alert icon (for issue counts)
 * - 'threshold': Dynamic color (green if 0, orange if >0), settings icon (for threshold-based metrics)
 * - 'requirement': Dynamic color (green if 0, orange if >0), checkbox icon (for requirement checks)
 * - 'success': Green color, checkbox icon (for positive metrics)
 * - 'info': Blue color, info icon (default for informational metrics)
 */
export type MetricType = 'total' | 'issues' | 'threshold' | 'info' | 'requirement' | 'success' | 'date_related';

export interface SummaryCardProps {
  title: string;
  value: number | string;  
  /** Optional subtitle text displayed in the footer with an icon */
  subtitle?: string;
  /** 
   * Determines the icon and color scheme of the card.
   * For 'issues', 'threshold', and 'requirement' types, colors are dynamic:
   * - Green when value is 0 (no issues)
   * - Orange when value > 0 (issues present)
   */
  metricType?: MetricType;
  /** Indicates if the card is clickable (adds hover effects and pointer cursor) */
  clickable?: boolean;
  /** Indicates if this card is the active filter (shows accent bar at top) */
  isActive?: boolean;
}

const SummaryCard: React.FC<SummaryCardProps> = ({
  title,
  value,  
  subtitle,
  metricType,
  clickable = false,
  isActive = false,
}) => {
  const theme = useTheme();
  
  const getMetricIcon = () => {
    switch (metricType) {
      case 'total':
        return <RiFileListLine />;
      case 'issues':
        return <RiAlertLine />;
      case 'threshold':
        return <RiSettings3Line />;
      case 'requirement':
        return <RiFileWarningLine />;
      case 'success':
        return <RiCheckboxCircleLine />;
      case 'info':
        return <RiInformationLine />;
      case 'date_related':
        return <RiCalendarLine />;
      default:
        return <RiInformationLine />;
    }
  };

  const getMetricColor = () => {
    switch (metricType) {
      case 'total':
        return theme.palette.primary.main;
      case 'issues':
        // Dynamic color based on value - green if 0, orange if issues exist
        return (typeof value === 'number' && value === 0) ? theme.palette.success.main : theme.palette.warning.main;
      case 'threshold':
        // Dynamic color based on value - green if 0, orange if issues exist
        return (typeof value === 'number' && value === 0) ? theme.palette.success.main : theme.palette.warning.main;
      case 'requirement':
        // Dynamic color based on value - green if 0, orange if issues exist
        return (typeof value === 'number' && value === 0) ? theme.palette.success.main : theme.palette.warning.main;
      case 'date_related':
        // Dynamic color based on value - green if 0, orange if issues exist
        return (typeof value === 'number' && value === 0) ? theme.palette.success.main : theme.palette.warning.main;
      case 'success':
        return theme.palette.success.main;
      case 'info':
        return theme.palette.primary.main;
      default:
        return theme.palette.primary.main;
    }
  };

  return (
    <Card
      sx={{
        width: "250px",
        height: "160px",
        margin: "8px",
        display: "inline-block",
        boxShadow: clickable ? 2 : 3,
        borderRadius: "12px",
        transition: "all 0.2s ease-in-out",
        cursor: clickable ? "pointer" : "default",
        border: clickable ? `1px solid ${theme.palette.divider}` : "none",
        position: "relative",
        overflow: "hidden",
        "&:hover": clickable ? {
          boxShadow: 4,
          transform: "translateY(-2px)",
          borderColor: theme.palette.primary.main,
        } : {},
      }}
    >
      {isActive && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 3,
            backgroundColor: "primary.main",
            zIndex: 1,
          }}
        />
      )}
      <CardContent
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          height: "100%",
          textAlign: "center",
          padding: "16px",
          "&:last-child": { paddingBottom: "16px" },
        }}
      >
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontWeight: "bold" }}
        >
          {title}
        </Typography>

        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          <Typography
            variant="h4"
            component="div"
            sx={{
              fontWeight: "bold",
            }}
          >
            {value}
          </Typography>

        </Box>

        {subtitle && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: getMetricColor(),
              mb: 1,
            }}
          >
            {getMetricIcon()}
            <Typography variant="body2" sx={{ ml: 0.5, color: 'text.secondary' }}>
              {subtitle}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default SummaryCard;
